export type RefillSessionStatus =
  | 'PICKING'
  | 'IN_TRANSIT'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'CANCELLED';

export type RefillListFilter =
  | 'all'
  | 'needs_action'
  | 'leftover'
  | 'in_transit'
  | 'completed'
  | 'today';

export type RefillCompletionSource = 'express' | 'cloud';

export interface RefillSessionListItem {
  id: string;
  machineId: string;
  machineName: string;
  venueName?: string;
  status: RefillSessionStatus;
  leftoverUnits: number;
  leftoversReturned: boolean;
  leftoverPending: boolean;
  needsAction: boolean;
  completionSource: RefillCompletionSource | null;
  createdAt: string;
  completedAt: string | null;
}

export interface RefillSessionListResult {
  items: RefillSessionListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export const REFILL_LIST_FILTERS: Array<{ value: RefillListFilter; label: string }> = [
  { value: 'needs_action', label: 'Needs action' },
  { value: 'in_transit', label: 'In Express' },
  { value: 'leftover', label: 'Leftover to return' },
  { value: 'today', label: 'Completed today' },
  { value: 'completed', label: 'Completed' },
  { value: 'all', label: 'All' },
];

export const ACTIVE_REFILL_SESSION_STATUSES = ['PICKING', 'IN_TRANSIT', 'IN_PROGRESS'] as const;

export function isActiveRefillSessionStatus(
  status: string
): status is (typeof ACTIVE_REFILL_SESSION_STATUSES)[number] {
  return status === 'PICKING' || status === 'IN_TRANSIT' || status === 'IN_PROGRESS';
}

export function refillStatusLabel(status: RefillSessionStatus): string {
  switch (status) {
    case 'PICKING':
      return 'Picking';
    case 'IN_TRANSIT':
      return 'In Express';
    case 'IN_PROGRESS':
      return 'Filling';
    case 'COMPLETED':
      return 'Completed';
    case 'CANCELLED':
      return 'Cancelled';
  }
}

export function refillSourceLabel(source: RefillCompletionSource | null): string {
  if (source === 'express') return 'Express';
  if (source === 'cloud') return 'Cloud';
  return '—';
}

export function refillSessionPath(sessionId: string): string {
  return `/refill-sessions/${sessionId}`;
}

export function allocatePickedToSlots(
  slots: ReadonlyArray<{ machineSlotId: string; quantityExpected: number }>,
  quantityPicked: number
): Record<string, number> {
  if (!Number.isFinite(quantityPicked) || quantityPicked < 0) {
    throw new Error('quantityPicked must be a non-negative number');
  }

  let remaining = Math.floor(quantityPicked);
  const allocated: Record<string, number> = {};

  for (const slot of slots) {
    if (typeof slot.machineSlotId !== 'string' || slot.machineSlotId.length === 0) {
      throw new Error('machineSlotId is required');
    }
    const expected = Number.isFinite(slot.quantityExpected)
      ? Math.max(0, Math.floor(slot.quantityExpected))
      : 0;
    const qty = Math.min(expected, remaining);
    allocated[slot.machineSlotId] = qty;
    remaining -= qty;
  }

  return allocated;
}

export function defaultLoadedQuantities(input: {
  lines: ReadonlyArray<{
    machineSlotId: string;
    productId: string | null;
    quantityExpected: number;
    quantityLoaded: number | null;
  }>;
  variance: ReadonlyArray<{ productId: string; quantityPicked: number }>;
}): Record<string, number> {
  const quantities: Record<string, number> = {};

  if (input.variance.length === 0) {
    for (const line of input.lines) {
      quantities[line.machineSlotId] = line.quantityLoaded ?? line.quantityExpected;
    }
    return quantities;
  }

  const pickedByProduct = new Map(
    input.variance.map((item) => [item.productId, item.quantityPicked])
  );
  const groups = new Map<string, Array<(typeof input.lines)[number]>>();

  for (const line of input.lines) {
    const key = line.productId ?? `slot:${line.machineSlotId}`;
    const group = groups.get(key) ?? [];
    group.push(line);
    groups.set(key, group);
  }

  Array.from(groups.values()).forEach((lines) => {
    const first = lines[0];
    if (!first) return;
    const picked = first.productId ? pickedByProduct.get(first.productId) : undefined;
    if (picked == null) {
      lines.forEach((line) => {
        quantities[line.machineSlotId] = line.quantityLoaded ?? 0;
      });
      return;
    }

    const allocated = allocatePickedToSlots(
      lines.map((line) => ({
        machineSlotId: line.machineSlotId,
        quantityExpected: line.quantityExpected,
      })),
      picked
    );
    lines.forEach((line) => {
      quantities[line.machineSlotId] = line.quantityLoaded ?? allocated[line.machineSlotId] ?? 0;
    });
  });

  return quantities;
}

export function formatRefillWhen(item: Pick<RefillSessionListItem, 'completedAt' | 'createdAt'>): string {
  const raw = item.completedAt ?? item.createdAt;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function emptyRefillListMessage(filter: RefillListFilter, machineScoped: boolean): string {
  if (machineScoped) {
    switch (filter) {
      case 'needs_action':
        return 'Nothing needs action on this machine.';
      case 'leftover':
        return 'No leftover stock waiting to return from this machine.';
      case 'in_transit':
        return 'No refill is waiting in Express for this machine.';
      case 'today':
        return 'No refill completed today on this machine.';
      case 'completed':
        return 'No completed refills for this machine.';
      case 'all':
        return 'No refill sessions for this machine yet.';
    }
  }

  switch (filter) {
    case 'needs_action':
      return 'Nothing needs action. Express refills and leftover returns will show here.';
    case 'leftover':
      return 'No leftover stock waiting to return to the hub.';
    case 'in_transit':
      return 'No refills waiting in Express.';
    case 'today':
      return 'No refills completed today.';
    case 'completed':
      return 'No completed refill sessions.';
    case 'all':
      return 'No refill sessions yet. Generate a picklist from a machine to start.';
  }
}
